import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { User } from '../types/entities'
import { apiClient, setTokenProvider, setCsrfToken, setRefreshHandler } from '../services/ApiClient'
import { setDisplayTimeZone } from '../utils/datetime'

interface AuthResponse {
  token: string | null // Nullable - now sent via HttpOnly cookie
  refreshToken: string | null // Nullable - now sent via HttpOnly cookie
  csrfToken: string | null // CSRF token for client to include in headers
  expiresAt: string
  user: User
  requiresMfa?: boolean // For MFA login flow
  requiresMfaSetup?: boolean // For admin users who must enable MFA before logging out
}

interface LoginRequest {
  email: string
  password: string
}

interface MfaLoginRequest {
  email: string
  code: string
}

interface RegisterRequest {
  email: string
  firstName: string
  lastName: string
  password: string
  confirmPassword: string
  phoneNumber?: string
  timeZone?: string
  companyName?: string
  marketingConsent?: boolean
  organizationSlug?: string
}

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  requiresMfaSetup: boolean
  login: (credentials: LoginRequest) => Promise<{ success: boolean; message: string; requiresMfa?: boolean }>
  loginWithMfa: (credentials: MfaLoginRequest) => Promise<{ success: boolean; message: string }>
  loginWithMicrosoft: (accessToken: string, companyName?: string, agreeToTerms?: boolean, marketingConsent?: boolean) => Promise<{ success: boolean; message: string }>
  register: (userData: RegisterRequest) => Promise<{ success: boolean; message: string }>
  logout: () => Promise<void>
  getAccessToken: () => string | null
  refetchUser: () => Promise<void>
  refreshToken: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [requiresMfaSetup, setRequiresMfaSetup] = useState(false)

  // Setup token provider and refresh handler for ApiClient (cookie-based auth, no bearer token)
  useEffect(() => {
    setTokenProvider(async () => null)
  }, [])

  // Load the tenant's configured display timezone (all roles) so dates render in
  // the tenant's zone, not each viewer's browser. Non-fatal: falls back to browser.
  const loadDisplaySettings = useCallback(async (): Promise<void> => {
    try {
      const ds = await apiClient.get<{ timeZone: string }>('/displaysettings')
      setDisplayTimeZone(ds?.timeZone)
    } catch {
      // ignore — formatter falls back to the browser zone
    }
  }, [])

  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticated) return false

    try {
      // Call refresh endpoint to get new JWT and extend session
      const authData = await apiClient.post<AuthResponse>('/auth/refresh', {})

      // Update CSRF token if provided
      if (authData.csrfToken) {
        setCsrfToken(authData.csrfToken)
      }

      // Update user data if provided
      if (authData.user) {
        setUser(authData.user)
      }

      return true
    } catch (error: any) {
      // If refresh fails (401 or session expired message), clear auth and redirect
      if (error.message?.includes('401') ||
          error.message?.includes('Unauthorized') ||
          error.message?.includes('Session has expired')) {
        clearAuthData()
        window.location.href = '/login'
        return false
      }
      return false
    }
  }, [isAuthenticated])

  // Register refresh handler with ApiClient
  useEffect(() => {
    setRefreshHandler(refreshToken)
  }, [refreshToken])

  // Initialize auth state from cookies
  useEffect(() => {
    const initializeAuth = async () => {
      // Skip auth validation on public pages
      const publicPaths = [
        '/login',
        '/signup',
        '/register',
        '/forgot-password',
        '/reset-password',
        '/terms',
        '/privacy',
        '/faq',
        '/accept-invitation',
        '/auth/accept-invitation',
        '/verify-email',
        '/auth/verify-email',
        '/auth/callback', // Microsoft SSO popup redirect URI
        '/welcome/activate', // New verification URL
        '/marketplace'
      ]
      const currentPath = window.location.pathname
      // Check for exact match of root, or if path starts with any public path
      const isPublicPage = currentPath === '/' || publicPaths.some(path => currentPath.startsWith(path))

      if (isPublicPage) {
        setIsLoading(false)
        return
      }

      try {
        // Try to verify authentication by fetching current user
        // If cookies are valid, this will succeed
        const userData = await apiClient.get<User>('/auth/me')
        setUser(userData)
        setIsAuthenticated(true)

        // Load tenant display timezone before rendering protected content so dates
        // render in the tenant's zone on first paint (not the viewer's browser zone).
        await loadDisplaySettings()

        // Check if admin user needs to set up MFA
        // This is required for security - admins cannot use the system without MFA
        if (userData.role === 'Admin' && !userData.mfaEnabled) {
          setRequiresMfaSetup(true)
        }
      } catch (error: any) {
        clearAuthData()

        // Redirect to login on 401 (only if not already on a public page)
        if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          const currentPath = window.location.pathname
          const publicRoutes = [
            '/', // Landing page
            '/login',
            '/signup',
            '/register',
            '/forgot-password',
            '/reset-password',
            '/terms',
            '/privacy',
            '/faq'
          ]
          const isPublicRoute = publicRoutes.some(route => currentPath === route || currentPath.includes(route))

          if (!isPublicRoute) {
            window.location.href = `/login?returnUrl=${encodeURIComponent(currentPath)}`
          }
        }
      }

      setIsLoading(false)
    }

    initializeAuth()
  }, [])

  // Activity-based token refresh
  // Refresh token when user is active and JWT is close to expiration
  useEffect(() => {
    if (!isAuthenticated) return

    let lastActivity = Date.now()
    let refreshTimer: NodeJS.Timeout | null = null

    // Activity detection events
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']

    const updateActivity = () => {
      lastActivity = Date.now()
    }

    // Add activity listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true })
    })

    // Check token expiration and refresh if needed
    const checkAndRefreshToken = async () => {
      const now = Date.now()
      const timeSinceActivity = now - lastActivity

      // If user has been inactive for more than 30 minutes, don't refresh
      const MAX_INACTIVITY_MS = 30 * 60 * 1000 // 30 minutes
      if (timeSinceActivity > MAX_INACTIVITY_MS) {
        console.log('[AuthContext] User inactive for 30+ minutes, session will expire')
        return
      }

      // Refresh token proactively if user is active
      try {
        await refreshToken()
        console.log('[AuthContext] Token refreshed due to user activity')
      } catch (error) {
        console.error('[AuthContext] Failed to refresh token:', error)
      }
    }

    // Set up periodic check (every 5 minutes)
    refreshTimer = setInterval(checkAndRefreshToken, 5 * 60 * 1000)

    // Cleanup
    return () => {
      if (refreshTimer) {
        clearInterval(refreshTimer)
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity)
      })
    }
  }, [isAuthenticated, refreshToken])

  const clearAuthData = () => {
    try {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
    } catch (e) {
      // localStorage blocked by browser privacy settings
      console.warn('localStorage access blocked:', e)
    }
    // Clear CSRF token from memory
    setCsrfToken(null)
    setUser(null)
    setIsAuthenticated(false)
  }

  const login = async (credentials: LoginRequest): Promise<{ success: boolean; message: string; requiresMfa?: boolean }> => {
    try {
      setIsLoading(true)

      // Backend expects PascalCase property names
      const loginData = {
        Email: credentials.email,
        Password: credentials.password
      }

      const authData = await apiClient.post<AuthResponse>('/auth/login', loginData)

      // Check if MFA is required
      if (authData.requiresMfa) {
        return {
          success: false,
          message: 'MFA verification required',
          requiresMfa: true
        }
      }

      // Store CSRF token in memory (tokens are now in HttpOnly cookies)
      if (authData.csrfToken) {
        setCsrfToken(authData.csrfToken)
      }

      // Set user state
      setUser(authData.user)
      setIsAuthenticated(true)
      await loadDisplaySettings()

      return { success: true, message: 'Login successful' }
    } catch (error: any) {
      console.error('Login error:', error.message || error)
      const message = error.message || 'Login failed. Please try again.'
      return { success: false, message }
    } finally {
      setIsLoading(false)
    }
  }

  const loginWithMfa = async (credentials: MfaLoginRequest): Promise<{ success: boolean; message: string }> => {
    try {
      setIsLoading(true)

      // Backend expects PascalCase property names
      const loginData = {
        Email: credentials.email,
        MfaCode: credentials.code
      }

      const authData = await apiClient.post<AuthResponse>('/auth/login/mfa', loginData)

      // Store CSRF token in memory (tokens are now in HttpOnly cookies)
      if (authData.csrfToken) {
        setCsrfToken(authData.csrfToken)
      }

      // Set user state
      setUser(authData.user)
      setIsAuthenticated(true)
      await loadDisplaySettings()

      return { success: true, message: 'Login successful' }
    } catch (error: any) {
      console.error('MFA login error:', error.message || error)
      const message = error.message || 'MFA verification failed. Please try again.'
      return { success: false, message }
    } finally {
      setIsLoading(false)
    }
  }

  const loginWithMicrosoft = async (
    accessToken: string,
    companyName?: string,
    agreeToTerms?: boolean,
    marketingConsent?: boolean
  ): Promise<{ success: boolean; message: string }> => {
    try {
      setIsLoading(true)

      // Pass access token directly — MSAL handles PKCE internally in loginPopup
      const callbackData = {
        AccessToken: accessToken,
        CompanyName: companyName,
        AgreeToTerms: agreeToTerms ?? false,
        MarketingConsent: marketingConsent ?? false
      }

      const authData = await apiClient.post<AuthResponse>('/auth/microsoft/callback', callbackData)

      // Store CSRF token in memory (tokens are now in HttpOnly cookies)
      if (authData.csrfToken) {
        setCsrfToken(authData.csrfToken)
      }

      // Set user state
      setUser(authData.user)
      setIsAuthenticated(true)

      // Check if admin user needs to set up MFA
      if (authData.user.role === 'Admin' && !authData.user.mfaEnabled) {
        setRequiresMfaSetup(true)
      }

      return { success: true, message: 'Microsoft login successful' }
    } catch (error: any) {
      console.error('Microsoft login error:', error.message || error)
      const message = error.message || 'Microsoft login failed. Please try again.'
      return { success: false, message }
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (userData: RegisterRequest): Promise<{ success: boolean; message: string }> => {
    try {
      setIsLoading(true)

      // Ensure TimeZone is set (use browser timezone or default to UTC)
      // Note: Backend expects PascalCase property names
      const registrationData = {
        Email: userData.email,
        FirstName: userData.firstName,
        LastName: userData.lastName,
        Password: userData.password,
        ConfirmPassword: userData.confirmPassword,
        PhoneNumber: userData.phoneNumber,
        TimeZone: userData.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        CompanyName: userData.companyName,
        MarketingConsent: userData.marketingConsent || false,
        OrganizationSlug: userData.organizationSlug
      }

      await apiClient.post<User>('/auth/register', registrationData)

      return {
        success: true,
        message: 'Registration successful! Please check your email to verify your account.'
      }
    } catch (error: any) {
      console.error('Registration error:', error.message || error)
      const message = error.message || 'Registration failed. Please try again.'
      return { success: false, message }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      // Call logout endpoint to invalidate token on server (if implemented)
      await apiClient.post<void>('/auth/logout', {})
    } catch (error: any) {
      console.error('Logout error:', error.message || error)
    } finally {
      clearAuthData()
    }
  }

  const getAccessToken = (): string | null => {
    return null
  }

  const refetchUser = async () => {
    try {
      const userData = await apiClient.get<User>('/auth/me')
      setUser(userData)

      // Update MFA setup flag if admin without MFA
      if (userData.role === 'Admin' && !userData.mfaEnabled) {
        setRequiresMfaSetup(true)
      } else {
        setRequiresMfaSetup(false)
      }
    } catch (error: any) {
      console.error('[AuthContext] Failed to refetch user:', error)
      // If we get 401, clear auth state
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        clearAuthData()
      }
    }
  }

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    requiresMfaSetup,
    login,
    loginWithMfa,
    loginWithMicrosoft,
    register,
    logout,
    getAccessToken,
    refetchUser,
    refreshToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
